import React, { ReactNode, useEffect, useState } from 'react';
import { Copy } from '@grubhubprod/gh-cookbook-react';
import { sdk } from 'src/utilities/sdk';
import { InvoiceRecipientForm } from 'src/components/enterprise/EnterpriseInvoiceRecipients/EnterpriseInvoiceRecipientForm';
import { pushErrorFromAPI } from 'src/utilities/notifications';

export interface InvoiceRecipientAutocompleteProps {
  name: keyof InvoiceRecipientForm;
  corpId: string;
  onSelect: (recipient: InvoiceRecipient) => void;
  children: ReactNode;
}

export const DEBOUNCE_SEARCH_TIMING = 300;

export interface InvoiceRecipient {
  email: string;
  first_name: string;
  last_name: string;
  invoice_contact_id: string;
  phone: string;
}

export const defaultFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
};

export const getRecipientsByQuery = async (
  corpId: string,
  query: string
): Promise<InvoiceRecipient[]> => {
  try {
    return await sdk.http.get(
      `/corps/${corpId}/invoice_contacts/autocomplete`,
      {
        searchParams: {
          query,
        },
      }
    );
  } catch (e) {
    pushErrorFromAPI(e);
    return [];
  }
};

export interface UseGetRecipients {
  recipients: InvoiceRecipient[];
}

export const useGetRecipients = (
  corpId: string,
  query: string,
  isDirty: boolean
): UseGetRecipients => {
  const [recipients, setRecipients] = useState<InvoiceRecipient[]>([]);

  const getRecipients = async () => {
    const response = await getRecipientsByQuery(corpId, query);
    setRecipients(response);
  };

  useEffect(() => {
    if (query && isDirty) {
      getRecipients();
    } else {
      setRecipients([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isDirty]);

  return { recipients };
};

export const getItemText = (
  name: keyof InvoiceRecipientForm,
  { first_name, last_name, email, phone }: InvoiceRecipient
): { primaryText: string; secondaryText: string } => {
  switch (name) {
    case 'lastName': {
      return {
        primaryText: last_name,
        secondaryText: `${first_name}, ${email}, ${phone}`,
      };
    }

    case 'email': {
      return {
        primaryText: email,
        secondaryText: `${first_name}, ${last_name}, ${phone}`,
      };
    }

    case 'firstName':
    default: {
      return {
        primaryText: first_name,
        secondaryText: `${last_name}, ${email}, ${phone}`,
      };
    }
  }
};

export const getItem = (
  name: keyof InvoiceRecipientForm,
  recipient: InvoiceRecipient
): JSX.Element => {
  const { primaryText, secondaryText } = getItemText(name, recipient);
  return (
    <>
      <Copy variant="body" color="primaryText">
        {primaryText},{' '}
      </Copy>
      <Copy variant="body" color="secondaryText">
        {secondaryText}
      </Copy>
    </>
  );
};
